import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  CREATE_DOCTOR_ADVICE,
  GET_ALL_DOCTOR_ADVICES,
  UPDATE_DOCTOR_ADVICE,
  DELETE_DOCTOR_ADVICE
} from '../graphql/doctorAdviceOperations';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const DoctorAdviceManagement = () => {
  // Theme hook
  const { isDarkMode } = useTheme();

  const [doctorAdviceData, setDoctorAdviceData] = useState({
    title: '',
    postContent: '',
    thumbnailURL: '',
    videoLink: ''
  });
  const [editingDoctorAdvice, setEditingDoctorAdvice] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Fetch all doctor advice
  const { loading, error, data, refetch } = useQuery(GET_ALL_DOCTOR_ADVICES, {
    onError: (error) => handleGraphQLError(error, 'Failed to load doctor advice')
  });

  // Mutations
  const [createDoctorAdvice] = useMutation(CREATE_DOCTOR_ADVICE, {
    onCompleted: () => {
      setDoctorAdviceData({ title: '', postContent: '', thumbnailURL: '', videoLink: '' });
      setIsCreateModalOpen(false);
      refetch();
      toast.success('Doctor Advice created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create doctor advice')
  });

  const [updateDoctorAdvice] = useMutation(UPDATE_DOCTOR_ADVICE, {
    onCompleted: () => {
      setEditingDoctorAdvice(null);
      setDoctorAdviceData({ title: '', postContent: '', thumbnailURL: '', videoLink: '' });
      refetch();
      toast.success('Doctor Advice updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update doctor advice')
  });

  const [deleteDoctorAdvice] = useMutation(DELETE_DOCTOR_ADVICE, {
    onCompleted: () => {
      refetch();
      toast.success('Doctor Advice deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete doctor advice')
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = ['title', 'postContent'];
    const missingFields = requiredFields.filter(field => !doctorAdviceData[field]);

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    const input = {
      title: doctorAdviceData.title || null,
      postContent: doctorAdviceData.postContent || null,
      thumbnailURL: doctorAdviceData.thumbnailURL || null,
      videoLink: doctorAdviceData.videoLink || null
    };

    try {
      let createdAdvice;
      if (editingDoctorAdvice) {
        // Update existing doctor advice
        createdAdvice = await updateDoctorAdvice({
          variables: {
            updateDoctorAdviceId: editingDoctorAdvice.id,
            input
          }
        });
      } else {
        // Create new doctor advice
        createdAdvice = await createDoctorAdvice({
          variables: { input }
        });
      }

      // Get the ID of the created/updated doctor advice
      const doctorAdviceId = createdAdvice.data.createDoctorAdvice.id;

      // Upload files if selected
      if (selectedThumbnail || selectedVideo) {
        const formData = new FormData();
        formData.append('doctorAdviceId', doctorAdviceId);

        if (selectedThumbnail) {
          formData.append('thumbnail', selectedThumbnail);
        }

        if (selectedVideo) {
          formData.append('video', selectedVideo);
        }

        try {
          const baseUrl = process.env.REACT_APP_API_BASE_URL;
          const uploadUrl = `${baseUrl}/doctor-advice/upload`;
          await axios.post(uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            }
          });

          toast.success('Files uploaded successfully');
        } catch (uploadError) {
          toast.error('Failed to upload files');
          console.error('File upload error:', uploadError);
        }
      }

      // Reset form and close modal
      setDoctorAdviceData({ title: '', postContent: '', thumbnailURL: '', videoLink: '' });
      setSelectedThumbnail(null);
      setSelectedVideo(null);
      setEditingDoctorAdvice(null);
      setIsCreateModalOpen(false);

      // Refetch the list
      refetch();
    } catch (error) {
      toast.error('Failed to create/update doctor advice');
      console.error('Mutation error:', error);
    }
  };

  const handleEdit = (doctorAdvice) => {
    setEditingDoctorAdvice(doctorAdvice);
    setDoctorAdviceData({
      title: doctorAdvice.title,
      postContent: doctorAdvice.postContent,
      thumbnailURL: doctorAdvice.thumbnailURL || '',
      videoLink: doctorAdvice.videoLink || ''
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this doctor advice?')) {
      deleteDoctorAdvice({
        variables: { deleteDoctorAdviceId: id }
      });
    }
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
    setDoctorAdviceData({ postContent: '', thumbnailURL: '', videoLink: '' });
    setEditingDoctorAdvice(null);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setDoctorAdviceData({ postContent: '', thumbnailURL: '', videoLink: '' });
    setEditingDoctorAdvice(null);
  };

  // Add file selection handlers
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    setSelectedThumbnail(file);
    setDoctorAdviceData(prev => ({
      ...prev,
      thumbnailURL: file ? URL.createObjectURL(file) : null
    }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    setSelectedVideo(file);
    setDoctorAdviceData(prev => ({
      ...prev,
      videoLink: file ? URL.createObjectURL(file) : null
    }));
  };

  // Video modal handler
  const handleVideoClick = (videoUrl) => {
    console.log('Video URL clicked:', videoUrl);
    setSelectedVideo(videoUrl);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsVideoModalOpen(false);
  };

  // Debugging log for data
  useEffect(() => {
    if (data && data.getAllDoctorAdvices) {
      console.log('Doctor Advice Data:', data.getAllDoctorAdvices.map(advice => ({
        id: advice.id,
        thumbnailURL: advice.thumbnailURL,
        videoLink: advice.videoLink
      })));
    }
  }, [data]);

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading doctor advice...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading doctor advice
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Create New Doctor Advice Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={openCreateModal}
          className={`
            flex items-center px-4 py-2 rounded-lg text-white font-semibold
            transition-all duration-300
            ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-500 hover:bg-blue-600'
            }
          `}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New Doctor Advice
        </button>
      </div>

      {/* Create/Edit Doctor Advice Modal */}
      {(isCreateModalOpen || editingDoctorAdvice) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className={`
              w-full max-w-md 
              ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
              rounded-2xl shadow-2xl border
              transition-colors duration-300
              ${editingDoctorAdvice
                ? 'ring-4 ring-blue-500/20'
                : 'ring-4 ring-green-500/20'
              }
            `}
          >
            <div className={`
              px-6 py-4 
              ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
              border-b 
              flex justify-between items-center
            `}>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {editingDoctorAdvice ? 'Edit Doctor Advice' : 'Create New Doctor Advice'}
              </h2>
              <button
                onClick={closeCreateModal}
                className={`
                  ${isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                  } 
                  transition
                `}
                title="Cancel"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  Title
                </label>
                <input
                  type="text"
                  value={doctorAdviceData.title}
                  onChange={(e) => setDoctorAdviceData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className={`
                    w-full px-4 py-2.5
                    ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                    }
                    border rounded-lg 
                    focus:outline-none focus:ring-2 
                    transition-colors duration-300
                  `}
                />
              </div>

              <div>
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  Post Content
                </label>
                <textarea
                  value={doctorAdviceData.postContent}
                  onChange={(e) => setDoctorAdviceData(prev => ({ ...prev, postContent: e.target.value }))}
                  placeholder="Enter post content"
                  required
                  className={`
                    w-full px-4 py-2.5 min-h-[100px]
                    ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                    }
                    border rounded-lg 
                    focus:outline-none focus:ring-2 
                    transition-colors duration-300
                  `}
                />
              </div>

              <div>
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  Thumbnail URL (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className={`
                    w-full px-3 py-2 
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-100 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border rounded-md
                  `}
                />
                {doctorAdviceData.thumbnailURL && (
                  <img
                    src={doctorAdviceData.thumbnailURL}
                    alt="Thumbnail Preview"
                    className="mt-2 h-24 w-auto object-cover rounded-md"
                  />
                )}
              </div>

              <div>
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  Video Links (Optional)
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  className={`
                    w-full px-3 py-2 
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-100 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border rounded-md
                  `}
                />
                {doctorAdviceData.videoLink && (
                  <video
                    src={doctorAdviceData.videoLink}
                    controls
                    className="mt-2 h-24 w-auto object-cover rounded-md"
                  />
                )}
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className={`
                    px-6 py-2.5 rounded-lg
                    ${isDarkMode
                      ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }
                    transition-colors duration-300
                  `}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`
                    inline-flex items-center justify-center 
                    px-6 py-2.5 rounded-lg text-white font-semibold
                    transition-all duration-300
                    ${editingDoctorAdvice
                      ? (isDarkMode
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-green-500 hover:bg-green-600')
                      : (isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-500 hover:bg-blue-600')
                    }
                  `}
                >
                  {editingDoctorAdvice ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Doctor Advice List Card */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Doctor Advice List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {data?.getAllDoctorAdvices?.length || 0} doctor advice
          </div>
        </div>

        {/* Doctor Advice Table */}
        <div className="overflow-x-auto">
          {data.getAllDoctorAdvices.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No doctor advice found
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className={`
                  ${isDarkMode
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
                  } 
                  text-sm
                `}>
                  <th className="px-6 py-3 text-left">Thumbnail</th>
                  <th className="px-6 py-3 text-left">Title</th>
                  <th className="px-6 py-3 text-left">Post Content</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.getAllDoctorAdvices.map((advice) => (
                  <tr
                    key={advice.id}
                    className={`
                      border-b 
                      ${isDarkMode
                        ? 'border-gray-700 hover:bg-gray-700'
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className="px-6 py-4">
                      {advice.thumbnailURL ? (
                        <div
                          className="relative w-16 h-16 cursor-pointer group"
                          onClick={() => {
                            // Construct full video URL if needed
                            const videoUrl = advice.videoLink
                              ? (advice.videoLink.startsWith('http')
                                ? advice.videoLink
                                : `${process.env.REACT_APP_API_BASE_URL}${advice.videoLink}`)
                              : null;

                            console.log('Thumbnail clicked', {
                              thumbnailURL: advice.thumbnailURL,
                              originalVideoLink: advice.videoLink,
                              constructedVideoUrl: videoUrl
                            });

                            videoUrl && handleVideoClick(videoUrl);
                          }}
                        >
                          {advice.videoLink && (
                            <div className="absolute inset-0 bg-black bg-opacity-30 
                              flex items-center justify-center opacity-0 
                              group-hover:opacity-100 transition-opacity duration-300 
                              rounded-md"
                            >
                              <PlayIcon className="w-8 h-8 text-white" />
                            </div>
                          )}
                          <img
                            src={advice.thumbnailURL.startsWith('http')
                              ? advice.thumbnailURL
                              : `${process.env.REACT_APP_API_BASE_URL}${advice.thumbnailURL}`}
                            alt={`${advice.postContent} thumbnail`}
                            className="w-16 h-16 object-cover rounded-md"
                          />
                        </div>
                      ) : (
                        <div className={`
                          w-16 h-16 flex items-center justify-center 
                          rounded-md 
                          ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}
                        `}>
                          <span className={`
                            text-xs 
                            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                          `}>
                            No Image
                          </span>
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {advice.title}
                    </td>
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {advice.postContent.length > 50
                        ? `${advice.postContent.slice(0, 50)}...`
                        : advice.postContent
                      }
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(advice.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(advice)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(advice.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`
            w-full max-w-2xl 
            ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
            rounded-2xl shadow-2xl border 
            transition-colors duration-300
            relative
          `}>
            <button
              onClick={closeVideoModal}
              className={`
                absolute top-4 right-4 z-10
                ${isDarkMode
                  ? 'text-gray-300 hover:text-gray-100'
                  : 'text-gray-600 hover:text-gray-900'
                }
                p-2 rounded-full hover:bg-gray-200/20
                transition
              `}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <video
              src={selectedVideo}
              controls
              autoPlay
              onError={(e) => {
                console.error('Video error:', {
                  error: e,
                  src: selectedVideo
                });
                toast.error('Failed to load video');
              }}
              className="w-full rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorAdviceManagement;
